// Построение хлебных крошек из текущего пути.
//
// Логика вынесена в чистую функцию, отделённую от рендера: маппинг
// «путь → цепочка крошек» юнит-тестируется без DOM, а `AppLayout`
// только превращает результат в `<Anchor>`/`<Text>`.
//
// Крошка несёт ключ i18n (а не готовую строку) — подписи живут в
// locales, функция остаётся языко-независимой. `to` задаётся только
// для крошек, у которых есть реальный навигуемый роут: «Настройки» —
// группирующий уровень без своей страницы, поэтому идёт без `to`
// (рендерится как текст). Последняя крошка — всегда не-ссылка
// (текущая страница), за это отвечает рендер в `AppLayout`.

export interface Crumb {
  /** Ключ i18n для подписи крошки. */
  readonly labelKey: string;
  /** Интерполяционные параметры для `t(labelKey, params)`. */
  readonly params?: Readonly<Record<string, string>>;
  /** Навигуемый путь; нет — крошка не кликабельна (нет своей страницы). */
  readonly to?: string;
}

// Префикс пути экрана одной задачи. `/tasks/:id` строит крошки
// «Очередь → Задача <id>», поэтому обрабатывается отдельно от
// статической таблицы.
const TASK_PREFIX = "/tasks/";

// Статическая таблица «путь → цепочка крошек». Пути без записи
// (напр. публичные `/`, `/login`) дают пустой массив — крошки не
// рендерятся.
const CRUMBS_BY_PATH: Readonly<Record<string, readonly Crumb[]>> = {
  "/queue": [{ labelKey: "nav.queue" }],
  "/self-improvement": [{ labelKey: "nav.selfImprovement" }],
  "/settings/organization": [
    { labelKey: "nav.settings" },
    { labelKey: "nav.settingsOrganization" },
  ],
  "/settings/connections": [
    { labelKey: "nav.settings" },
    { labelKey: "nav.settingsConnections" },
  ],
  "/settings/runners": [
    { labelKey: "nav.settings" },
    { labelKey: "nav.settingsRunners" },
  ],
  "/settings/trust-rules": [
    { labelKey: "nav.settings" },
    { labelKey: "nav.settingsTrustRules" },
  ],
  "/settings/agent-roles": [
    { labelKey: "nav.settings" },
    { labelKey: "nav.settingsAgentRoles" },
  ],
  "/settings/reviewer-checklist": [
    { labelKey: "nav.settings" },
    { labelKey: "nav.settingsReviewerChecklist" },
  ],
  "/settings/tool-allowlist": [
    { labelKey: "nav.settings" },
    { labelKey: "nav.settingsToolAllowlist" },
  ],
  "/settings/tool-blacklist": [
    { labelKey: "nav.settings" },
    { labelKey: "nav.settingsToolBlacklist" },
  ],
  "/settings/branch-name-template": [
    { labelKey: "nav.settings" },
    { labelKey: "nav.settingsBranchNameTemplate" },
  ],
  "/settings/repository-settings": [
    { labelKey: "nav.settings" },
    { labelKey: "nav.settingsRepositorySettings" },
  ],
};

/**
 * Построить цепочку хлебных крошек для пути.
 *
 * `/tasks/:id` → «Очередь» (ссылка на `/queue`) + «Задача <id>».
 * Прочие пути — из статической таблицы; неизвестный путь → `[]`.
 */
export function buildCrumbs(pathname: string): readonly Crumb[] {
  if (pathname.startsWith(TASK_PREFIX)) {
    const id = pathname.slice(TASK_PREFIX.length);
    return [
      { labelKey: "nav.queue", to: "/queue" },
      { labelKey: "nav.task", params: { id } },
    ];
  }
  return CRUMBS_BY_PATH[pathname] ?? [];
}
